import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { ConversationSummary } from '../types';
import './MapView.scss';

// Read HERE API key from runtime config
const _cfg = (window as any)._adminEnv_ || {};
const HERE_API_KEY = _cfg.REACT_APP_HERE_API_KEY || '';

function getTileConfig(): { url: string; attribution: string } {
  if (HERE_API_KEY) {
    return {
      url: `https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/png8?style=explore.day&apiKey=${HERE_API_KEY}`,
      attribution: '&copy; HERE Maps',
    };
  }
  // CartoDB Positron – clean light-gray minimal style matching Figma design
  return {
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
  };
}

// Custom phone marker icon
function createPhoneIcon(isSelected: boolean) {
  const size = isSelected ? 40 : 34;
  const bg = isSelected ? '#5b21b6' : '#009966'; // purple when selected, green otherwise
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2}" fill="${bg}" />
      <g transform="translate(${size / 2 - 7}, ${size / 2 - 7})">
        <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z" fill="white" transform="scale(0.875)"/>
      </g>
    </svg>
  `;
  return L.divIcon({
    html: svg,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
  });
}

// Component to pan map to selected conversation
interface PanToProps {
  lat: number;
  lon: number;
}
function PanTo({ lat, lon }: PanToProps) {
  const map = useMap();
  useEffect(() => {
    map.setView([lat, lon], Math.max(map.getZoom(), 13), { animate: true });
  }, [lat, lon, map]);
  return null;
}

interface MapViewProps {
  conversations: ConversationSummary[];
  selectedId?: string | null;
  onSelectConversation: (conv: ConversationSummary) => void;
  onResolve: (id: string) => void;
}

export default function MapView({ conversations, selectedId, onSelectConversation, onResolve }: MapViewProps) {
  const tile = getTileConfig();
  const markersRef = useRef<Map<string, L.Marker>>(new Map());

  const pinned = conversations.filter(
    c => c.location_lat != null && c.location_lon != null
  );

  const selected = pinned.find(c => c.id === selectedId);

  return (
    <div className="map-view-container">
      <MapContainer
        center={[25.2048, 55.2708]}
        zoom={11}
        className="map-leaflet"
        zoomControl={true}
      >
        <TileLayer url={tile.url} attribution={tile.attribution} />

        {selected && selected.location_lat != null && selected.location_lon != null && (
          <PanTo lat={selected.location_lat} lon={selected.location_lon} />
        )}

        {pinned.map(conv => {
          const isSelected = conv.id === selectedId;
          const icon = createPhoneIcon(isSelected);
          return (
            <Marker
              key={conv.id}
              position={[conv.location_lat!, conv.location_lon!]}
              icon={icon}
              ref={(marker) => {
                if (marker) {
                  markersRef.current.set(conv.id, marker);
                  if (isSelected) {
                    marker.openPopup();
                  }
                }
              }}
              eventHandlers={{
                click: () => onSelectConversation(conv),
              }}
            >
              <Popup className="map-pin-popup" minWidth={240}>
                <MapPinPopupContent
                  conv={conv}
                  onOpen={() => onSelectConversation(conv)}
                  onResolve={() => onResolve(conv.id)}
                />
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}

// Inline popup content
interface PopupContentProps {
  conv: ConversationSummary;
  onOpen: () => void;
  onResolve: () => void;
}
function MapPinPopupContent({ conv, onOpen, onResolve }: PopupContentProps) {
  const name = conv.user_name || conv.user_id || 'Unknown';
  const dateStr = conv.created_at
    ? new Date(conv.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' ' +
      new Date(conv.created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : '—';

  return (
    <div className="pin-popup">
      <button className="pin-popup-open" onClick={onOpen}>
        <div className="pin-popup-name">{name}</div>
        {conv.incident_number && (
          <div className="pin-popup-incident">{conv.incident_number}</div>
        )}
        <div className="pin-popup-date">{dateStr}</div>
        {conv.location_address && (
          <div className="pin-popup-addr">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            {conv.location_address}
          </div>
        )}
        {conv.user_phone && (
          <div className="pin-popup-phone">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.39 2 2 0 0 1 3.6 1.22h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.16 6.16l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
            {conv.user_phone}
          </div>
        )}
      </button>
      <button
        className="pin-popup-resolve"
        onClick={(e) => { e.stopPropagation(); onResolve(); }}
      >
        Mark as resolved
      </button>
    </div>
  );
}
